/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Fri Jan 12 2024
 **/

import React, { forwardRef, PropsWithChildren, ReactElement, Ref } from "react";

import { useDragging } from "../hooks";

// function 선언자로 컴포넌트 작성 시 컴파운트 패턴의 특성상
// Dragger.d.ts 파일에서 var 선언으로 인한 에러 발생하여 const 선언자로 컴포넌트 작성했습니다.

interface DragProps {
	children: React.ReactNode;
	zIndex: number;
	startCoordinate?: { x: number; y: number };
	position?: { x: "left" | "right"; y: "top" | "bottom" };
	className?: string;
	onPointerDown?: (ev: React.PointerEvent) => void;
	onPointerUp?: (ev: React.PointerEvent) => void;
	onCoordinateChanged?(coordinate: { x: number; y: number }): void;
}

const Dragger = <T,>(props: PropsWithChildren<DragProps> & T, ref: Ref<HTMLDivElement>) => {
	const { listeners, coordinate } = useDragging(props.startCoordinate, props.position, {
		onCoordinateChanged: props.onCoordinateChanged
	});

	const onPointerDown = (ev: React.PointerEvent) => {
		const target = ev.target as HTMLElement;

		if (target.classList.contains("undraggable")) return;

		const undraggableList = document.querySelectorAll(".undraggable");

		let drag = true;

		undraggableList.forEach((undraggable) => {
			if (undraggable.contains(target)) {
				drag = false;
			}
		});

		if (!drag) return;

		listeners.onPointerDown(ev);
		props.onPointerDown?.(ev);
	};

	const onPointerUp = (ev: React.PointerEvent) => {
		listeners.onPointerUp(ev);
		props.onPointerUp?.(ev);
	};

	return (
		<div
			ref={ref}
			className={props.className}
			css={{
				position: "fixed",
				[props.position?.y ?? "top"]: `${coordinate.y}px`,
				[props.position?.x ?? "left"]: `${coordinate.x}px`,
				zIndex: props.zIndex
			}}
			{...listeners}
			onPointerDown={onPointerDown}
			onPointerUp={onPointerUp}
		>
			{props.children}
		</div>
	);
};

export const UnDraggable = ({ children }: { children: React.ReactElement | React.ReactElement[] }) => {
	return React.Children.map(children, (child) =>
		React.cloneElement(child, {
			className: `${child.props.className ?? ""} undraggable`
		})
	);
};

export default forwardRef(Dragger) as <T>(
	props: PropsWithChildren<DragProps> & T & { ref?: Ref<HTMLDivElement> }
) => ReactElement;
